import React, {PropsWithChildren} from "react";
import {Box, Button, Tooltip} from "@material-ui/core";
import {Link} from "react-router-dom";

interface PageHeaderProps {
    title: string;
}

export const PageHeader: React.FC<PropsWithChildren<PageHeaderProps>> = (props: PropsWithChildren<PageHeaderProps>) => {
    return (
        <Box display="flex" alignItems="center" style={{marginBottom: "1.5rem"}}>
            <Box flexGrow={1}>
                <h1 style={{margin: 0, fontSize: "1.75rem", fontWeight: 700, color: "#111827"}}>{props.title}</h1>
            </Box>
            <Box display="flex" alignItems="center" style={{gap: "8px"}}>
                {props.children}
            </Box>
        </Box>
    );
};

interface PageActionProps {
    title: string;
    last?: boolean;
    disabled?: boolean;
    color?: "primary" | "secondary" | "inherit" | "default";
    size?: "small" | "medium" | "large";
    to?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
    children: NonNullable<React.ReactNode>;
    external?: boolean;
    variant?: "contained" | "outlined" | "text";
}

export const PageAction: React.FC<PageActionProps> = (props: PageActionProps) => {
    const style = {
        ...props.style,
        marginRight: props.last ? "0" : "8px",
        borderRadius: "8px",
        textTransform: "none" as const,
        fontWeight: 600,
        gap: "6px",
    };
    const variant = props.variant ?? "contained";
    const muiColor = props.color === "default" ? "default" : props.color ?? "primary";

    let button = null;
    if (props.to === undefined) {
        button = (
            <Button
                color={muiColor}
                size={props.size === "large" ? "large" : props.size === "small" ? "small" : "medium"}
                variant={variant}
                onClick={props.onClick}
                style={style}
                disabled={props.disabled}
                startIcon={props.children}
            >
                {props.title}
            </Button>
        );
    } else if (props.external) {
        button = (
            <Button
                color={muiColor}
                size={props.size === "large" ? "large" : props.size === "small" ? "small" : "medium"}
                variant={variant}
                href={props.to}
                style={style}
                disabled={props.disabled}
                startIcon={props.children}
            >
                {props.title}
            </Button>
        );
    } else {
        button = (
            <Link to={props.to} style={{textDecoration: "none"}}>
                <Button
                    color={muiColor}
                    size={props.size === "large" ? "large" : props.size === "small" ? "small" : "medium"}
                    variant={variant}
                    style={style}
                    disabled={props.disabled}
                    startIcon={props.children}
                >
                    {props.title}
                </Button>
            </Link>
        );
    }
    return button;
};

interface PrimaryPageActionProps {
    title: string;
    disabled?: boolean;
    to?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
    children: NonNullable<React.ReactNode>;
    external?: boolean;
}

export const PrimaryPageAction: React.FC<PrimaryPageActionProps> = (props: PrimaryPageActionProps) => {
    return <PageAction color="primary" size="large" variant="contained" last {...props} />;
};

interface SecondaryPageActionProps {
    title: string;
    disabled?: boolean;
    to?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
    children: NonNullable<React.ReactNode>;
    external?: boolean;
}

export const SecondaryPageAction: React.FC<SecondaryPageActionProps> = (props: SecondaryPageActionProps) => {
    return <PageAction color="default" size="medium" variant="outlined" {...props} />;
};
